import {
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  Button,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Text,
  Tooltip,
  Flex,
  Box,
  useToast,
  useDisclosure,
} from '@chakra-ui/react';
import {
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getSortedRowModel,
  Row,
  RowSelectionState,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import * as _ from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import {
  date as commonDate,
  typeguards as commonTypeguards,
  utils as commonUtils,
} from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import {
  BorrowingBaseTestTypeGQLType,
  ReceivableBorrowingBaseTestWaiverGQLType,
  ReceivableBorrowingBaseTestWaiverInputGQLType,
  ReceivableIdGroupWithLoanProductGQLType,
} from 'types/dashboard-core-gqlTypes.generated.d';

import { getNumberOfSelectedUniqueWaiverRows } from './utils';
import { VerificationBulkDeleteWaiverModal } from './VerificationBulkDeleteWaiverModal';
import { VerificationBulkUpdateWaiverModal } from './VerificationBulkUpdateWaiverModal';
import { AddReceivableWaiverModal } from './VerificationReceivableEligibilityAddWaiverModal';
import {
  ColumnFilter,
  formatBaseColumnDef,
  ManageWaiverBulkActionRow,
  renderCheckboxHeaderCell,
  renderSelectCell,
} from './VerificationReceivableManageWaiverComponents';
import { VerificationBorrowerRecentWaiverUpdateIcon } from './VerificationTableSharedComponents';
import {
  GetReceivableWaiversForCreditFacilityWithOrganizationDocumentGQLType,
  GetReceivableWaiversForReceivableIdsDocumentGQLType,
  useDeleteReceivableWaiversMutation,
  useUpdateReceivableWaiversMutation,
} from 'packages/borrowing-base/requests/borrowingBase.gql.generated';
import { colors } from 'packages/common-frontend/colors';
import { Icon } from 'packages/common-frontend/components/icons';
import {
  errorToastConfig,
  errorToastId,
  successToastConfig,
} from 'packages/common-frontend/constants';
import { FaSort, FaSortDown, FaSortUp } from 'react-icons/fa';

type VerificationReceivableEligibilityUpdateWaiversDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  receivableWaivers: ReceivableBorrowingBaseTestWaiverGQLType[];
  creditFacilityId: string;
  loanProductId: string;
  loanProductReceivableIdGroups: ReceivableIdGroupWithLoanProductGQLType[];
  selectedReceivableIdGroups?: ReceivableIdGroupWithLoanProductGQLType[];
  setHaveWaiversBeenUpdated?: React.Dispatch<React.SetStateAction<boolean>>;
  reportingDate: Util.FullDateStr;
  borrowerOrganizationName: string;
};
const VerificationReceivableEligibilityUpdateWaiversDrawer = ({
  isOpen,
  onClose,
  receivableWaivers,
  creditFacilityId,
  loanProductId,
  loanProductReceivableIdGroups,
  selectedReceivableIdGroups,
  setHaveWaiversBeenUpdated,
  reportingDate,
  borrowerOrganizationName,
}: VerificationReceivableEligibilityUpdateWaiversDrawerProps) => {
  const toast = useToast();
  const btnRef = useRef(null);
  const {
    isOpen: isAddWaiverModalOpen,
    onOpen: onAddWaiverModalOpen,
    onClose: onAddWaiverModalClose,
  } = useDisclosure();
  const {
    isOpen: isBulkUpdateWaiverModalOpen,
    onOpen: onBulkUpdateWaiverModalOpen,
    onClose: onBulkUpdateWaiverModalClose,
  } = useDisclosure();
  const {
    isOpen: isBulkDeleteWaiverModalOpen,
    onOpen: onBulkDeleteWaiverModalOpen,
    onClose: onBulkDeleteWaiverModalClose,
  } = useDisclosure();

  const [
    queuedReceivableWaiverUpdates,
    setQueuedReceivableWaiverUpdates,
  ] = useState<ReceivableBorrowingBaseTestWaiverInputGQLType[]>([]);
  const [
    queuedReceivableWaiverDeletions,
    setQueuedReceivableWaiverDeletions,
  ] = useState<ReceivableBorrowingBaseTestWaiverInputGQLType[]>([]);
  const [tableData, setTableData] = useState<
    ReceivableBorrowingBaseTestWaiverInputGQLType[]
  >([...receivableWaivers, ...queuedReceivableWaiverUpdates]);

  // tanstack table column state mgmt
  const [columnVisibility] = useState<{
    [key: string]: boolean;
  }>({
    finleyReceivableId: false,
  });
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [rowSelectionState, setRowSelectionState] = useState<RowSelectionState>(
    {},
  );

  // make sure table data is updated with queue changes
  useEffect(() => {
    setTableData([...receivableWaivers, ...queuedReceivableWaiverUpdates]);
  }, [receivableWaivers, queuedReceivableWaiverUpdates]);

  const table = useReactTable({
    data: tableData,
    columns: [
      {
        id: 'select',
        cell: ({
          row,
        }: {
          row: Row<ReceivableBorrowingBaseTestWaiverInputGQLType>;
        }) => renderSelectCell({ row }),
        header: ({ column, table: tableInstance }) =>
          renderCheckboxHeaderCell({
            tableInstance,
            column,
            setData: ({ data }) => setTableData(data),
            currentData: tableData,
          }),
      },
      formatBaseColumnDef({
        accessorKey: 'updated_at',
        header: 'Updated At',
      }),
      formatBaseColumnDef({
        accessorKey: 'lastUpdatedOrganizationName',
        header: 'Updated By',
      }),
      formatBaseColumnDef({
        accessorKey: 'externalReceivableId',
        header: 'Receivable ID',
      }),
      {
        id: 'finleyReceivableId',
        accessorKey: 'finleyReceivableId',
      },
      formatBaseColumnDef({
        accessorKey: 'borrowingBaseTestType',
        header: 'Type',
      }),
      formatBaseColumnDef({
        accessorKey: 'borrowingBaseTestKey',
        header: 'Test',
      }),
      formatBaseColumnDef({
        accessorKey: 'waiverStartReportingDate',
        header: 'Start Date',
      }),
      formatBaseColumnDef({
        accessorKey: 'waiverExpirationReportingDate',
        header: 'End Date',
      }),
    ],
    state: {
      columnVisibility,
      sorting,
      columnFilters,
      rowSelection: rowSelectionState,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onRowSelectionChange: setRowSelectionState,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    debugTable: true,
    debugHeaders: true,
    debugColumns: true,
    enableRowSelection: true,
    enableMultiRowSelection: true,
  });

  const onCloseDrawer = useCallback(() => {
    // close drawer and reset state
    onClose();
    setQueuedReceivableWaiverUpdates([]);
    setQueuedReceivableWaiverDeletions([]);
    setSorting([]);
    setColumnFilters([]);
    setRowSelectionState({});
  }, [onClose]);

  const [updateReceivables] = useUpdateReceivableWaiversMutation({
    refetchQueries: [
      GetReceivableWaiversForReceivableIdsDocumentGQLType,
      GetReceivableWaiversForCreditFacilityWithOrganizationDocumentGQLType,
    ],
    onCompleted: () => {
      if (!toast.isActive(successToastConfig.id)) {
        successToastConfig.title = 'Successfully updated waivers';
        toast(successToastConfig);
      }
    },
    onError: () => {
      if (!toast.isActive(errorToastId)) {
        errorToastConfig.title = 'Error updating waivers. Please try again.';
        toast(errorToastConfig);
      }
    },
  });

  const [deleteReceivables] = useDeleteReceivableWaiversMutation({
    refetchQueries: [
      GetReceivableWaiversForReceivableIdsDocumentGQLType,
      GetReceivableWaiversForCreditFacilityWithOrganizationDocumentGQLType,
    ],
    onCompleted: () => {
      if (!toast.isActive(successToastConfig.id)) {
        successToastConfig.title = 'Successfully updated waivers';
        toast(successToastConfig);
      }
    },
    onError: () => {
      if (!toast.isActive(errorToastId)) {
        errorToastConfig.title = 'Error updating waivers. Please try again.';
        toast(errorToastConfig);
      }
    },
  });

  const onDeleteWaiverRow = useCallback(
    ({
      finleyReceivableId,
      borrowingBaseTestType,
      borrowingBaseTestKey,
    }: {
      finleyReceivableId: string;
      borrowingBaseTestType: BorrowingBaseTestTypeGQLType;
      borrowingBaseTestKey: string;
    }) => {
      const newDeletionQueue = [...queuedReceivableWaiverDeletions];
      const newUpdateQueue = [...queuedReceivableWaiverUpdates];

      // check if in queued waivers
      const existingWaiverInUpdateQueueIndex = _.findIndex(
        queuedReceivableWaiverUpdates,
        {
          finleyReceivableId,
          creditFacilityId,
          borrowingBaseTestType,
          borrowingBaseTestKey,
        },
      );
      if (existingWaiverInUpdateQueueIndex !== -1) {
        // if waiver to delete exists in the update queue
        // remove from update queue
        newUpdateQueue.splice(existingWaiverInUpdateQueueIndex, 1);
        setQueuedReceivableWaiverUpdates(newUpdateQueue);
      }

      // check if waiver is already in active waivers
      const existingActiveWaiver = _.find(receivableWaivers, {
        finleyReceivableId,
        creditFacilityId,
        borrowingBaseTestType,
        borrowingBaseTestKey,
      });
      if (existingActiveWaiver) {
        // if waiver exists in the stored active waivers
        // check if receivable is already in deletion queue
        const existingWaiverInDeletionQueueIndex = _.findIndex(
          queuedReceivableWaiverDeletions,
          {
            finleyReceivableId,
            creditFacilityId,
            borrowingBaseTestType,
            borrowingBaseTestKey,
          },
        );
        if (existingWaiverInDeletionQueueIndex === -1) {
          const {
            id: _id,
            created_at: _created_at,
            updated_at: _updated_at,
            __typename: _typename,
            ...existingActiveWaiverInputs
          } = existingActiveWaiver;
          // add to deletion queue if not already in it
          newDeletionQueue.push(existingActiveWaiverInputs);
          setQueuedReceivableWaiverDeletions(newDeletionQueue);
        }
      }
    },
    [
      creditFacilityId,
      receivableWaivers,
      queuedReceivableWaiverUpdates,
      queuedReceivableWaiverDeletions,
    ],
  );

  const onUpdateReceivables = useCallback(() => {
    if (queuedReceivableWaiverUpdates.length) {
      updateReceivables({
        variables: {
          creditFacilityId,
          receivableWaivers: queuedReceivableWaiverUpdates,
        },
      });
    }
    if (queuedReceivableWaiverDeletions.length) {
      deleteReceivables({
        variables: {
          creditFacilityId,
          receivableWaivers: queuedReceivableWaiverDeletions,
        },
      });
    }

    if (
      setHaveWaiversBeenUpdated &&
      (queuedReceivableWaiverUpdates.length ||
        queuedReceivableWaiverDeletions.length)
    ) {
      setHaveWaiversBeenUpdated(true);
    }

    onCloseDrawer();
  }, [
    queuedReceivableWaiverUpdates,
    updateReceivables,
    queuedReceivableWaiverDeletions,
    deleteReceivables,
    creditFacilityId,
    setHaveWaiversBeenUpdated,
    onCloseDrawer,
  ]);

  const onClickBulkEdit = useCallback(() => {
    if (table.getSelectedRowModel().rows.length > 0) {
      onBulkUpdateWaiverModalOpen();
    }
  }, [table, onBulkUpdateWaiverModalOpen]);

  const onClickBulkDelete = useCallback(() => {
    if (table.getSelectedRowModel().rows.length > 0) {
      onBulkDeleteWaiverModalOpen();
    }
  }, [table, onBulkDeleteWaiverModalOpen]);

  return (
    <Drawer
      isOpen={isOpen}
      placement="right"
      onClose={onCloseDrawer}
      finalFocusRef={btnRef}
    >
      <DrawerOverlay />
      <DrawerContent maxW="1200px">
        <DrawerCloseButton />
        <DrawerHeader>
          <Text>Waiver Queue</Text>
          <Text fontSize="14px" fontWeight={400}>
            Select the waivers you would like to edit.
          </Text>
        </DrawerHeader>

        <DrawerBody>
          <AddReceivableWaiverModal
            isOpen={isAddWaiverModalOpen}
            onClose={onAddWaiverModalClose}
            receivableIdGroups={loanProductReceivableIdGroups}
            creditFacilityId={creditFacilityId}
            loanProductId={loanProductId}
            queuedReceivableWaiverUpdates={queuedReceivableWaiverUpdates}
            setQueuedReceivableWaiverUpdates={setQueuedReceivableWaiverUpdates}
            queuedReceivableWaiverDeletions={queuedReceivableWaiverDeletions}
            setQueuedReceivableWaiverDeletions={
              setQueuedReceivableWaiverDeletions
            }
            activeReceivableWaivers={receivableWaivers}
            preSelectedReceivableOptions={selectedReceivableIdGroups}
          />
          <Box
            borderWidth="medium"
            borderColor={colors.gray[200]}
            // maxH required for table top sticky header to work
            // since position:sticky attaches to the nearest ancestor with a scrolling mechanism
            // https://stackoverflow.com/questions/70770027/table-with-sticky-header-and-horizontal-scroll
            maxHeight="50vh"
            minHeight="50vh"
            overflowX="auto"
            overflowY="auto"
            position="relative"
            mt="16px"
          >
            <Table style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
              <Thead>
                {_.map(table.getHeaderGroups(), (headerGroup) => (
                  <Tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      const columnCanSort = header.column.getCanSort();
                      const columnCanFilter = header.column.getCanFilter();
                      return (
                        <Th
                          key={header.id}
                          fontSize="12px"
                          p="12px"
                          position="sticky"
                          top="0"
                          zIndex={3}
                        >
                          {header.isPlaceholder ? null : (
                            <Box>
                              <Flex alignItems="center">
                                <Box
                                  cursor={columnCanSort ? 'pointer' : 'default'}
                                  onClick={header.column.getToggleSortingHandler()}
                                  mr="8px"
                                >
                                  {{
                                    asc: <FaSortUp />,
                                    desc: <FaSortDown />,
                                  }[header.column.getIsSorted() as string] ??
                                    (columnCanSort ? <FaSort /> : null)}
                                </Box>

                                <Box mr="8px">
                                  {flexRender(
                                    header.column.columnDef.header,
                                    header.getContext(),
                                  )}
                                </Box>

                                {columnCanFilter && (
                                  <ColumnFilter
                                    column={header.column}
                                    currentColumnFilters={columnFilters}
                                    table={table}
                                  />
                                )}
                              </Flex>
                            </Box>
                          )}
                        </Th>
                      );
                    })}
                    {/* Column for delete button */}
                    <Th position="sticky" top="0" zIndex={3} />
                  </Tr>
                ))}
              </Thead>
              <Tbody>
                <ManageWaiverBulkActionRow
                  numberOfSelectedWaivers={getNumberOfSelectedUniqueWaiverRows(
                    table,
                  )}
                  colSpan={table.getVisibleLeafColumns().length + 1} // include the last delete icon column
                  onClickBulkEdit={onClickBulkEdit}
                  onClickBulkDelete={onClickBulkDelete}
                />
                {_.map(table.getRowModel().rows, (row) => {
                  const finleyReceivableId: string = row.getValue(
                    'finleyReceivableId',
                  );
                  const borrowingBaseTestType: BorrowingBaseTestTypeGQLType = row.getValue(
                    'borrowingBaseTestType',
                  );
                  const borrowingBaseTestKey: string = row.getValue(
                    'borrowingBaseTestKey',
                  );
                  const waiverStartReportingDate: Util.Maybe<Util.FullDateStr> = row.getValue(
                    'waiverStartReportingDate',
                  );
                  const waiverExpirationReportingDate: Util.Maybe<Util.FullDateStr> = row.getValue(
                    'waiverExpirationReportingDate',
                  );
                  const updatedAt: number = row.getValue('updated_at');
                  const lastUpdatedOrganizationName: string = row.getValue(
                    'lastUpdatedOrganizationName',
                  );

                  const isWaiverUpdatedAfterReportingDate = Boolean(
                    updatedAt > commonDate.fullDateStrToEpoch(reportingDate),
                  );
                  const isLastUpdatedByBorrowerOrganization = Boolean(
                    borrowerOrganizationName === lastUpdatedOrganizationName,
                  );

                  const existingStoredWaiver = _.find(
                    receivableWaivers,
                    (receivableWaiver) => {
                      return (
                        receivableWaiver.finleyReceivableId ===
                          finleyReceivableId &&
                        receivableWaiver.borrowingBaseTestType ===
                          borrowingBaseTestType &&
                        receivableWaiver.borrowingBaseTestKey ===
                          borrowingBaseTestKey
                      );
                    },
                  );

                  const existingUpdateQueuedWaiver = _.find(
                    queuedReceivableWaiverUpdates,
                    (queuedReceivableWaiverUpdate) => {
                      return (
                        queuedReceivableWaiverUpdate.finleyReceivableId ===
                          finleyReceivableId &&
                        queuedReceivableWaiverUpdate.borrowingBaseTestType ===
                          borrowingBaseTestType &&
                        queuedReceivableWaiverUpdate.borrowingBaseTestKey ===
                          borrowingBaseTestKey
                      );
                    },
                  );

                  const existingDeletionQueuedWaiver = _.find(
                    queuedReceivableWaiverDeletions,
                    (queuedReceivableWaiverDeletion) => {
                      return (
                        queuedReceivableWaiverDeletion.finleyReceivableId ===
                          finleyReceivableId &&
                        queuedReceivableWaiverDeletion.borrowingBaseTestType ===
                          borrowingBaseTestType &&
                        queuedReceivableWaiverDeletion.borrowingBaseTestKey ===
                          borrowingBaseTestKey
                      );
                    },
                  );

                  // if in deletion queue, don't render the waiver
                  if (existingDeletionQueuedWaiver) {
                    return null;
                  }

                  // if row exists in both stored waivers and update queue
                  // do not render the one in the stored waiver
                  // only render the one in the update queue since it has the latest dates
                  if (existingStoredWaiver && existingUpdateQueuedWaiver) {
                    const isExistingStoredWaiverRecord =
                      existingStoredWaiver.waiverStartReportingDate ===
                        waiverStartReportingDate &&
                      existingStoredWaiver.waiverExpirationReportingDate ===
                        waiverExpirationReportingDate;

                    if (isExistingStoredWaiverRecord) {
                      return null;
                    }
                  }

                  // set global row color to warning if it's a brand new waiver in queue
                  // that doesn't already exist in the stored waivers from db
                  const globalRowColor = existingStoredWaiver
                    ? undefined
                    : colors.warning;

                  return (
                    <Tr key={row.id} verticalAlign="top">
                      {_.map(row.getVisibleCells(), (cell) => {
                        const cellValue = cell.getValue();
                        let formattedCellValue = cell.column.columnDef.cell;

                        let cellColor: string | undefined;

                        const displayNewWaiverAlertIcon =
                          cell.column.id === 'updated_at' &&
                          isWaiverUpdatedAfterReportingDate &&
                          isLastUpdatedByBorrowerOrganization;

                        switch (cell.column.id) {
                          case 'borrowingBaseTestType':
                          case 'borrowingBaseTestKey':
                            if (typeof cellValue === 'string') {
                              formattedCellValue = commonUtils.toTitleCase(
                                cellValue,
                              );
                            }
                            break;
                          case 'waiverStartReportingDate':
                            if (commonTypeguards.isFullDateStr(cellValue)) {
                              formattedCellValue = commonDate.fullDateStrToSlashedDateStr(
                                cellValue,
                                'M/d/yy',
                              );
                            }
                            if (
                              existingStoredWaiver &&
                              existingUpdateQueuedWaiver
                            ) {
                              // if start date has changed from the existing stored waiver
                              // add individual cell color
                              cellColor =
                                cellValue ===
                                existingStoredWaiver.waiverStartReportingDate
                                  ? undefined
                                  : colors.warning;
                            }
                            break;
                          case 'waiverExpirationReportingDate':
                            if (commonTypeguards.isFullDateStr(cellValue)) {
                              formattedCellValue = commonDate.fullDateStrToSlashedDateStr(
                                cellValue,
                                'M/d/yy',
                              );
                            }
                            if (
                              existingStoredWaiver &&
                              existingUpdateQueuedWaiver
                            ) {
                              // if expiration date has changed from the existing stored waiver
                              // add individual cell color
                              cellColor =
                                cellValue ===
                                existingStoredWaiver.waiverExpirationReportingDate
                                  ? undefined
                                  : colors.warning;
                            }
                            break;
                          case 'updated_at':
                            if (typeof cellValue === 'number') {
                              formattedCellValue = commonDate.epochToSlashedDateStr(
                                cellValue,
                              );
                            }
                            break;
                          default:
                            formattedCellValue = cell.column.columnDef.cell;
                        }

                        return (
                          <Td
                            key={cell.id}
                            fontSize="12px"
                            p="12px"
                            color={globalRowColor ?? cellColor}
                          >
                            {displayNewWaiverAlertIcon && (
                              <VerificationBorrowerRecentWaiverUpdateIcon />
                            )}
                            {flexRender(formattedCellValue, cell.getContext())}
                          </Td>
                        );
                      })}
                      <Td verticalAlign="top">
                        <Tooltip label="Delete waiver">
                          <Button
                            variant="ghost"
                            onClick={() =>
                              onDeleteWaiverRow({
                                finleyReceivableId,
                                borrowingBaseTestType,
                                borrowingBaseTestKey,
                              })
                            }
                          >
                            <Icon icon="trash" color={colors.gray[500]} />
                          </Button>
                        </Tooltip>
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </Box>
          {/* Bulk Edit and delete modals */}
          {receivableWaivers && !!receivableWaivers.length && (
            <Box>
              <VerificationBulkUpdateWaiverModal
                isOpen={isBulkUpdateWaiverModalOpen}
                onClose={onBulkUpdateWaiverModalClose}
                queuedReceivableWaiverUpdates={queuedReceivableWaiverUpdates}
                setQueuedReceivableWaiverUpdates={
                  setQueuedReceivableWaiverUpdates
                }
                activeReceivableWaivers={receivableWaivers}
                selectedRows={table.getSelectedRowModel().rows}
                resetRowSelection={() => table.resetRowSelection()}
              />
              <VerificationBulkDeleteWaiverModal
                isOpen={isBulkDeleteWaiverModalOpen}
                onClose={onBulkDeleteWaiverModalClose}
                queuedReceivableWaiverUpdates={queuedReceivableWaiverUpdates}
                setQueuedReceivableWaiverUpdates={
                  setQueuedReceivableWaiverUpdates
                }
                queuedReceivableWaiverDeletions={
                  queuedReceivableWaiverDeletions
                }
                setQueuedReceivableWaiverDeletions={
                  setQueuedReceivableWaiverDeletions
                }
                activeReceivableWaivers={receivableWaivers}
                selectedRows={table.getSelectedRowModel().rows}
                resetRowSelection={() => table.resetRowSelection()}
                numberOfUniqueSelectedWaivers={getNumberOfSelectedUniqueWaiverRows(
                  table,
                )}
              />
            </Box>
          )}
          <Flex justifyContent="flex-end" my="32px">
            {/* Add Button */}
            <Tooltip
              label={`Add Waiver${
                selectedReceivableIdGroups ? ' for selected receivables' : ''
              }`}
            >
              <Button
                variant="outline"
                colorScheme="aurora"
                borderWidth="2px"
                onClick={onAddWaiverModalOpen}
              >
                <Flex alignItems="center">
                  <Icon icon="plus" />
                  <Text ml="8px">Add Waiver</Text>
                </Flex>
              </Button>
            </Tooltip>
          </Flex>
        </DrawerBody>

        <DrawerFooter>
          <Button
            colorScheme="aurora"
            variant="outline"
            borderWidth="2px"
            mr="32px"
            onClick={onCloseDrawer}
          >
            Cancel
          </Button>
          <Button colorScheme="aurora" onClick={onUpdateReceivables}>
            Save
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

type ReceivableEligibilityUpdateWaiversDrawerContainerForSpecificReceivablesProps = {
  isOpen: boolean;
  onClose: () => void;
  creditFacilityId: string;
  loanProductId: string;
  reportingDate: Util.FullDateStr;
  selectedReceivableIdGroups: ReceivableIdGroupWithLoanProductGQLType[];
  loanProductReceivableIdGroups: ReceivableIdGroupWithLoanProductGQLType[];
  receivableWaiversForLoanProduct: ReceivableBorrowingBaseTestWaiverGQLType[];
  setHaveWaiversBeenUpdated?: React.Dispatch<React.SetStateAction<boolean>>;
  borrowerOrganizationName: string;
};
const ReceivableEligibilityUpdateWaiversDrawerContainerForSpecificReceivables = ({
  isOpen,
  onClose,
  creditFacilityId,
  loanProductId,
  reportingDate,
  selectedReceivableIdGroups,
  loanProductReceivableIdGroups,
  receivableWaiversForLoanProduct,
  setHaveWaiversBeenUpdated,
  borrowerOrganizationName,
}: ReceivableEligibilityUpdateWaiversDrawerContainerForSpecificReceivablesProps) => {
  const selectedFinleyReceivableIds = _.map(
    selectedReceivableIdGroups,
    ({ finleyReceivableId }) => finleyReceivableId,
  );

  const receivableWaiversForSelectedReceivables = _.filter(
    receivableWaiversForLoanProduct,
    (waiver) =>
      _.includes(selectedFinleyReceivableIds, waiver.finleyReceivableId),
  );
  return (
    <VerificationReceivableEligibilityUpdateWaiversDrawer
      isOpen={isOpen}
      onClose={onClose}
      receivableWaivers={receivableWaiversForSelectedReceivables}
      creditFacilityId={creditFacilityId}
      loanProductId={loanProductId}
      reportingDate={reportingDate}
      loanProductReceivableIdGroups={loanProductReceivableIdGroups}
      selectedReceivableIdGroups={selectedReceivableIdGroups}
      setHaveWaiversBeenUpdated={setHaveWaiversBeenUpdated}
      borrowerOrganizationName={borrowerOrganizationName}
    />
  );
};

type ReceivableEligibilityUpdateWaiversDrawerContainerForCreditFacilityProps = {
  isOpen: boolean;
  onClose: () => void;
  creditFacilityId: string;
  loanProductId: string;
  reportingDate: Util.FullDateStr;
  loanProductReceivableIdGroups: ReceivableIdGroupWithLoanProductGQLType[];
  receivableWaiversForLoanProduct: ReceivableBorrowingBaseTestWaiverGQLType[];
  setHaveWaiversBeenUpdated?: React.Dispatch<React.SetStateAction<boolean>>;
  borrowerOrganizationName: string;
};
const ReceivableEligibilityUpdateWaiversDrawerContainerForCreditFacility = ({
  isOpen,
  onClose,
  creditFacilityId,
  loanProductId,
  reportingDate,
  loanProductReceivableIdGroups,
  receivableWaiversForLoanProduct,
  setHaveWaiversBeenUpdated,
  borrowerOrganizationName,
}: ReceivableEligibilityUpdateWaiversDrawerContainerForCreditFacilityProps) => {
  return (
    <VerificationReceivableEligibilityUpdateWaiversDrawer
      isOpen={isOpen}
      onClose={onClose}
      receivableWaivers={receivableWaiversForLoanProduct}
      creditFacilityId={creditFacilityId}
      loanProductId={loanProductId}
      loanProductReceivableIdGroups={loanProductReceivableIdGroups}
      setHaveWaiversBeenUpdated={setHaveWaiversBeenUpdated}
      reportingDate={reportingDate}
      borrowerOrganizationName={borrowerOrganizationName}
    />
  );
};

type VerificationReceivableEligibilityUpdateWaiversDrawerContainerProps = {
  isOpen: boolean;
  onClose: () => void;
  creditFacilityId: string;
  loanProductId: string;
  reportingDate: Util.FullDateStr;
  selectedReceivableIdGroups?: ReceivableIdGroupWithLoanProductGQLType[];
  loanProductReceivableIdGroups: ReceivableIdGroupWithLoanProductGQLType[];
  receivableWaiversForLoanProduct: ReceivableBorrowingBaseTestWaiverGQLType[];
  setHaveWaiversBeenUpdated?: React.Dispatch<React.SetStateAction<boolean>>;
  borrowerOrganizationName: string;
};
export const VerificationReceivableEligibilityUpdateWaiversDrawerContainer = ({
  isOpen,
  onClose,
  creditFacilityId,
  loanProductId,
  reportingDate,
  selectedReceivableIdGroups,
  loanProductReceivableIdGroups,
  receivableWaiversForLoanProduct,
  setHaveWaiversBeenUpdated,
  borrowerOrganizationName,
}: VerificationReceivableEligibilityUpdateWaiversDrawerContainerProps) => {
  if (selectedReceivableIdGroups) {
    return (
      <ReceivableEligibilityUpdateWaiversDrawerContainerForSpecificReceivables
        isOpen={isOpen}
        onClose={onClose}
        creditFacilityId={creditFacilityId}
        loanProductId={loanProductId}
        reportingDate={reportingDate}
        selectedReceivableIdGroups={selectedReceivableIdGroups}
        loanProductReceivableIdGroups={loanProductReceivableIdGroups}
        receivableWaiversForLoanProduct={receivableWaiversForLoanProduct}
        setHaveWaiversBeenUpdated={setHaveWaiversBeenUpdated}
        borrowerOrganizationName={borrowerOrganizationName}
      />
    );
  }

  return (
    <ReceivableEligibilityUpdateWaiversDrawerContainerForCreditFacility
      isOpen={isOpen}
      onClose={onClose}
      creditFacilityId={creditFacilityId}
      loanProductId={loanProductId}
      reportingDate={reportingDate}
      loanProductReceivableIdGroups={loanProductReceivableIdGroups}
      receivableWaiversForLoanProduct={receivableWaiversForLoanProduct}
      setHaveWaiversBeenUpdated={setHaveWaiversBeenUpdated}
      borrowerOrganizationName={borrowerOrganizationName}
    />
  );
};
